// Custom dropdown — a styleable replacement for a native <select>, built
// first for the stamp-count picker (KoffeeKat feedback, 2026-08-15) so it
// can look/feel like part of the app instead of an OS-chrome popup. Kept
// generic (typed on the option value) so other native selects across
// owner/staff/admin/superadmin can adopt it later without a rewrite.
//
// WAI-ARIA "select-only combobox" pattern: a button trigger + a role="listbox"
// popup, arrow keys move the active option, Enter/Space commits, Escape and
// outside-click/tap close without changing the value.

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import './dropdown.css'

export interface DropdownOption<T extends string> {
  value: T
  label: string
}

export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className = '',
  renderTrigger,
}: {
  value: T
  options: DropdownOption<T>[]
  onChange: (value: T) => void
  ariaLabel: string
  className?: string
  /** Custom trigger content — defaults to the selected option's label. */
  renderTrigger?: (selected: DropdownOption<T> | undefined) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  )
  const [activeIndex, setActiveIndex] = useState(selectedIndex)
  const [listStyle, setListStyle] = useState<CSSProperties | undefined>(
    undefined,
  )

  // Outside click/tap closes without committing — pointerdown (not click) so
  // it fires before a tap on another control re-opens something else.
  useEffect(() => {
    if (!open) return
    function onDocPointer(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onDocPointer)
    return () => document.removeEventListener('pointerdown', onDocPointer)
  }, [open])

  // Re-sync the active (keyboard-highlighted) option to the current value
  // every time the popup opens, and move focus into the listbox so arrow
  // keys work immediately.
  useEffect(() => {
    if (open) {
      setActiveIndex(selectedIndex)
      listRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Position the popup in the viewport instead of trusting the CSS default
  // (centered directly under the trigger). A trigger near the bottom of the
  // screen (the stamp-count stepper, which sits low in a bottom-sheet
  // overlay) or near the left edge (the Logs range picker) otherwise gets
  // its popup clipped by the viewport edge. Measured with the popup's real
  // rendered size via useLayoutEffect so the fix-up happens before paint —
  // no visible jump from the CSS fallback position to the computed one.
  useLayoutEffect(() => {
    if (!open) {
      setListStyle(undefined)
      return
    }
    const trigger = triggerRef.current
    const list = listRef.current
    if (!trigger || !list) return

    const margin = 8
    const gap = 6
    const desiredMaxHeight = 240
    const vw = window.innerWidth
    const vh = window.innerHeight
    const triggerRect = trigger.getBoundingClientRect()
    const listRect = list.getBoundingClientRect()

    const spaceBelow = vh - triggerRect.bottom - gap - margin
    const spaceAbove = triggerRect.top - gap - margin
    const openUp = listRect.height > spaceBelow && spaceAbove > spaceBelow
    const maxHeight = Math.max(
      120,
      Math.min(desiredMaxHeight, openUp ? spaceAbove : spaceBelow),
    )
    const effHeight = Math.min(listRect.height, maxHeight)

    let left = triggerRect.left + triggerRect.width / 2 - listRect.width / 2
    left = Math.max(margin, Math.min(left, vw - listRect.width - margin))

    setListStyle({
      position: 'fixed',
      transform: 'none',
      left,
      maxHeight,
      top: openUp ? Math.max(margin, triggerRect.top - effHeight - gap) : triggerRect.bottom + gap,
    })
  }, [open])

  function commit(index: number) {
    const opt = options[index]
    if (!opt) return
    onChange(opt.value)
    setOpen(false)
  }

  function onListKeyDown(e: KeyboardEvent<HTMLUListElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((i) => Math.min(options.length - 1, i + 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((i) => Math.max(0, i - 1))
        break
      case 'Home':
        e.preventDefault()
        setActiveIndex(0)
        break
      case 'End':
        e.preventDefault()
        setActiveIndex(options.length - 1)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        commit(activeIndex)
        break
      case 'Escape':
      case 'Tab':
        setOpen(false)
        break
    }
  }

  const selected = options[selectedIndex]

  return (
    <div className={`dropdown ${className}`} ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
          }
        }}
      >
        {renderTrigger ? renderTrigger(selected) : (selected?.label ?? '')}
      </button>
      {open && (
        <ul
          ref={listRef}
          className="dropdown-list"
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
          style={listStyle}
          onKeyDown={onListKeyDown}
        >
          {options.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              className={`dropdown-option${i === activeIndex ? ' is-active' : ''}${
                o.value === value ? ' is-selected' : ''
              }`}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => commit(i)}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
